import { ColumnDef, FilterFn } from '@tanstack/react-table';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { Col, Cell } from '@/components/ui/DataTable/ColumnsHelpers';
import cellType from '@/components/ui/DataTable/CellTypes';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import { isAptOrder } from './aptStandingOrders';
import styles from './StandingOrdersPage.module.scss';
import {
  ActiveStatus,
  AttentionFilter,
  ATTENTION_FILTERS,
  LastChargeStatus,
  activeStatusKey,
  compareNextChargeDateSort,
  describeAutoFreeze,
  formatCardExpiry,
  formatDate,
  formatRowMoney,
  getActiveStatus,
  getCardExpiryState,
  getCreationDateRaw,
  getEndDate,
  getLastChargeStatus,
  getNextChargeDate,
  getPaymentSourceLabel,
  getPaymentsPaid,
  getPaymentsTotal,
  getTotalPaid,
  getVisibleLastChargeDate,
  isInactive,
  isOutOfView,
  isUnlimited,
  matchesAttention,
  parseDate,
  STANDING_ORDER_TYPE_BANK,
  STANDING_ORDER_TYPE_CREDIT,
} from './standingOrderModel';

/*
 * What a standing-orders board is made of - its columns, colours, search, filters and export -
 * shared by the coordinator's board (StandingOrdersPage) and the office's by-donor screen
 * (HAT-10024), so an order reads the same on both and a change to one is a change to both.
 * Moved here as they were; the board's behaviour is unchanged.
 */

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** What the column builders need from the page that draws them. */
export interface BoardColumnContext {
  t: Translate;
  locale: string;
  /** «מוקפאת» rather than «לא פעילה» wherever the board itself can freeze. */
  frozenTerm: boolean;
}

export function getCreationDate(row: NedarimStandingOrderDtoIn, locale: string): string | null {
  const d = getCreationDateRaw(row);
  return d ? formatDate(d, locale) : null;
}

export function formatPaymentStatus(row: NedarimStandingOrderDtoIn, t: Translate): string {
  const paid = getPaymentsPaid(row);
  const remaining = row.remainingPayments;

  // 999 is Nedarim's "no end". Adding it to the paid count produced "14 of 1013 paid".
  if (remaining == null || isUnlimited(remaining)) {
    return t('standingOrders.paidUnlimited', { paid });
  }

  return t('standingOrders.paidOf', { paid, total: getPaymentsTotal(row) ?? paid + remaining });
}

export const LAST_CHARGE_COLORS: Record<LastChargeStatus, string> = {
  succeeded: 'var(--status-approved-border)',
  failed: 'var(--status-rejected-border)',
  none: 'var(--border-low)',
  inactive: 'var(--border-low)',
};

export const ACTIVE_COLORS: Record<ActiveStatus, string> = {
  enabled: 'var(--status-approved-border)',
  /*
   * Blue, not the amber it was: the «פג תוקף בקרוב» pill and the card's «פג בקרוב» tag are amber,
   * and a frozen order sat in the same colour beside them (HAT-1447).
   */
  disabled: 'var(--status-pending-completion-border)',
  ended: 'var(--status-cancelled-border)',
  /*
   * Waiting for the bank to approve: the amber of "pending approval". It was blue until HAT-1447 gave
   * the blue to a frozen order, which the bank board shows too.
   */
  awaitingBank: 'var(--status-pending-border)',
  // A bank order cancelled from the board (HAT-1441), in the colour the blade's panel gives «בוטלה».
  cancelled: 'var(--status-rejected-border)',
};

/**
 * A card that is expired or about to. `getCardExpiryState` answers null when the row has no
 * expiry at all - a bank order, or a card the accounting system holds without a date - and null
 * is not a warning: comparing it to 'valid' put a «פג בקרוב» tag on every such row.
 */
export const cardNeedsAttention = (row: NedarimStandingOrderDtoIn): boolean => {
  const state = getCardExpiryState(row.validity);
  return state === 'expired' || state === 'expiringSoon';
};

/** yyyy-MM-dd read in local time, so a filter edge means the same day the user picked. */
export function parseIsoDate(value: string): Date | null {
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return null;
  const d = new Date(+parts[1], +parts[2] - 1, +parts[3]);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * One line by design. The row height is fixed by the density setting and fed to the
 * virtualizer, so a cell that grows to two lines overlaps the row beneath it and swallows its
 * clicks. Anything that does not fit on the line goes in the tooltip.
 */
export const StatusCell = ({ id, color, label, note }: {
  id: string; color: string; label: string; note?: string;
}) => (
  <>
    <span id={id} className="flex items-center gap-2 min-w-0">
      <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="truncate">{label}</span>
    </span>
    {note && <CustomTooltip targetId={id}>{note}</CustomTooltip>}
  </>
);

/** The board's search. `more` adds what a particular board also lets you type, such as the fund. */
export const makeStandingOrderSearch = <T extends NedarimStandingOrderDtoIn>(
  locale: string,
  autoFrozenText = '',
  more?: (order: T) => (string | null | undefined)[],
): FilterFn<T> =>
  (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const order = row.original;

  const searchableFields = [
    order.clientName,
    order.israeliId,
    order.phone,
    order.email,
    getCreationDate(order, locale),
    order.amount?.toString(),
    order.nextDate,
    order.comments,
    order.lastNum,
    order.accountDetails,
    // «ביטול הרשאה» in the search box finds the orders stopped for it (HAT-1439).
    order.aptStopReason,
    // «הוקפאה אוטומטית» finds the orders the declined-orders check froze (HAT-1447).
    order.autoFreeze ? autoFrozenText : null,
    // The office's donor number, which tells two donors of the same name apart (HAT-1488).
    order.aptContactId != null ? String(order.aptContactId) : null,
    ...(more ? more(order) : []),
  ];

  return searchableFields.some((field) => field?.toLowerCase().includes(searchValue));
  };

// ------------------------------------------------------------------------------------- filters

/** The board's filter row, as values. */
export interface BoardFilters {
  attention: ReadonlySet<AttentionFilter>;
  dateRange: { from: string; to: string } | null;
  /** A search looks through every order, frozen and finished ones too (isOutOfView). */
  searching: boolean;
}

/** Which board is asking, for the questions the two boards answer differently. */
export interface BoardKind {
  isBank: boolean;
  /** The pill in the second slot: a card about to run out, or a bank order about to run out of payments. */
  needsSecondPill: (order: NedarimStandingOrderDtoIn) => boolean;
}

/** The rows the filter row lets through. */
export function filterBoardRows<T extends NedarimStandingOrderDtoIn>(rows: T[], filters: BoardFilters, board: BoardKind): T[] {
  const { attention, dateRange, searching } = filters;
  return rows.filter((order) => {
    if (isOutOfView(order, attention, { isBank: board.isBank, searching })) return false;

    if (dateRange) {
      // Built from parts rather than new Date('yyyy-MM-dd'), which is midnight UTC and
      // therefore the evening before in Israel - orders slid across the range boundary.
      const filterFrom = parseIsoDate(dateRange.from);
      const filterTo = parseIsoDate(dateRange.to);

      if (filterFrom && filterTo) {
        filterTo.setHours(23, 59, 59, 999);

        const startDate = getCreationDateRaw(order);
        const endDate = getEndDate(order);

        if (startDate && !(startDate <= filterTo && (endDate === null || endDate >= filterFrom))) {
          return false;
        }
      }
    }

    // The pills add up, like the status pills on every other board: "failed" and
    // "expiring" shows the orders in either group, not only those in both.
    if (attention.size > 0 && ![...attention].some((key) => matchesAttention(order, key, board))) return false;

    return true;
  });
}

/**
 * The rows each pill opens on its own. A frozen or finished order with a dead card is not counted
 * under «פג תוקף בקרוב» on the credit board, which does not show it there - the number on a pill
 * is the number of rows it opens.
 */
export function countAttention(rows: readonly NedarimStandingOrderDtoIn[] | undefined, board: BoardKind): Record<AttentionFilter, number> {
  const counts: Record<AttentionFilter, number> = { ok: 0, failed: 0, expiring: 0, frozen: 0, ended: 0 };
  if (!rows) return counts;

  for (const order of rows) {
    const outOfDefaultView = !board.isBank && isInactive(order);
    for (const key of ATTENTION_FILTERS) {
      if (key === 'expiring' && outOfDefaultView) continue;
      if (matchesAttention(order, key, board)) counts[key]++;
    }
  }
  return counts;
}

/**
 * What a coordinator opens the board to find out. The card count beside it is the
 * actionable half: a failure behind a dead card is one the donor has to fix, and
 * until now nothing on the page said so.
 */
export function summarizeFailures(rows: readonly NedarimStandingOrderDtoIn[] | undefined): { failed: number; expiringOrExpired: number } {
  let failed = 0;
  let expiringOrExpired = 0;

  for (const order of rows ?? []) {
    if (getLastChargeStatus(order) !== 'failed') continue;
    failed++;
    if (cardNeedsAttention(order)) expiringOrExpired++;
  }

  return { failed, expiringOrExpired };
}

// ------------------------------------------------------------------------------------- columns
/*
 * Called on every render, never memoised: Col() and Cell() call hooks inside (see the note in
 * StandingOrdersPage), so they must run in the same order each time.
 */

export function donorColumn<T extends NedarimStandingOrderDtoIn>({ t }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'clientName',
    header: Col(t('standingOrders.donor')),
    accessorFn: (row) => row.clientName,
    cell: ({ row }) => {
      const order = row.original;
      const source = getPaymentSourceLabel(order);
      const expiry = formatCardExpiry(order.validity);
      const expiryState = getCardExpiryState(order.validity);
      const isCredit = order.type === STANDING_ORDER_TYPE_CREDIT;

      let sourceLine: string;
      if (isCredit) {
        if (!source) sourceLine = t('standingOrders.credit');
        // When the tag is shown it carries the date (and its tooltip spells it out), so the
        // line does not repeat it - with both, the card number was what got cut off.
        else if (expiry && expiryState === 'valid') sourceLine = t('standingOrders.sourceCredit', { source, expiry });
        else sourceLine = t('standingOrders.sourceCreditPlain', { source });
      } else if (order.type === STANDING_ORDER_TYPE_BANK) {
        // On a board that holds bank orders only, the word «בנקאי» says nothing; the account does.
        sourceLine = !source ? t('standingOrders.bank')
          : isAptOrder(order) ? t('standingOrders.sourceAccount', { account: source })
            : t('standingOrders.sourceBank', { account: source });
      } else {
        sourceLine = order.type;
      }
      // A credit order the office charges itself, beside Nedarim's: no Nedarim actions apply to it.
      if (isCredit && isAptOrder(order)) sourceLine = `${sourceLine} · ${t('standingOrders.viaOffice')}`;

      const cardWarning = cardNeedsAttention(order);
      const tagId = `card-expiry-${order.standingOrderId}`;
      const expiryTitle = t('standingOrders.cardExpiresOn', { expiry });
      const tagLabel = expiryState === 'expired'
        ? t('standingOrders.expired') : t('standingOrders.expiringSoon');

      return (
        <span className={styles.cellStack}>
          <span className={styles.cellMain}>{order.clientName}</span>
          <span
            className={`${styles.subLine} ${cardWarning ? styles.subLineWarn : ''}`}
          >
            <span className={styles.subLineText}>{sourceLine}</span>
            {cardWarning && (
              <>
                {/* tabIndex so the tooltip opens on focus too - the component listens for it,
                    but a span is not focusable on its own. aria-label carries the date
                    regardless, so a screen reader does not depend on the tooltip appearing. */}
                <span
                  id={tagId}
                  className={expiryState === 'expired' ? styles.expiredTag : styles.expiryTag}
                  tabIndex={0}
                  aria-label={`${tagLabel} - ${expiryTitle}`}
                >
                  {tagLabel}
                </span>
                <CustomTooltip targetId={tagId}>{expiryTitle}</CustomTooltip>
              </>
            )}
          </span>
        </span>
      );
    },
    meta: { className: 'min-w-[200px] w-[19.4%]', mobileClassName: 'max-w-[38%] min-w-[38%] w-[38%]' },
  };
}

export function paymentAmountColumn<T extends NedarimStandingOrderDtoIn>({ t, locale }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'paymentAmount',
    header: Col(t('standingOrders.paymentAmount')),
    accessorFn: (row) => formatRowMoney(row.amount, row, locale),
    sortingFn: (a, b) => a.original.amount - b.original.amount,
    cell: Cell(cellType.String),
    meta: { className: 'min-w-[120px] w-[10.4%]', mobileClassName: 'max-w-[22%] min-w-[22%] w-[22%]' },
  };
}

export function lastChargeColumn<T extends NedarimStandingOrderDtoIn>({ t, locale }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'lastCharge',
    header: Col(t('standingOrders.lastCharge')),
    accessorFn: (row) => getLastChargeStatus(row),
    /*
     * By the date the column shows, and only by that. It sorted by how much a row wants
     * attention once, and then by the last charge date - but a failed row has no date on
     * screen, so ordering it by one made the board look unsorted: failures landed between
     * dated rows for a reason nothing visible could explain. A row with no date to show now
     * sorts as having none and gathers at the end of the default order, which is newest
     * first. The banner and the "נכשל" pill are how a coordinator reaches them.
     */
    sortingFn: (a, b) => {
      const dateA = getVisibleLastChargeDate(a.original)?.getTime() ?? -Infinity;
      const dateB = getVisibleLastChargeDate(b.original)?.getTime() ?? -Infinity;
      return dateA - dateB;
    },
    cell: ({ row }) => {
      const order = row.original;
      const status = getLastChargeStatus(order);
      // Nedarim give a reason on credit only; the accounting system gives the bank's on a return.
      const reason = status === 'failed' ? (order.errorMessage || null) : null;
      // A bank charge does not fail, it comes back from the bank: the row says so in the bank's word.
      const statusLabel = status === 'failed' && order.type === STANDING_ORDER_TYPE_BANK
        ? t('standingOrders.lastCharge_returned')
        : t(`standingOrders.lastCharge_${status}`);
      // The same date the column sorts by, so the two can never tell different stories.
      const lastDate = getVisibleLastChargeDate(order);
      const date = lastDate ? formatDate(lastDate, locale) : null;
      const id = `last-charge-${order.standingOrderId}`;

      /*
       * A stopped order reports the date it last collected and nothing else - no status
       * word, no dot, nothing to act on.
       */
      if (status === 'inactive') {
        return <span className="truncate">{date ?? '-'}</span>;
      }

      /*
       * The date belongs to the charge the row is reporting, and only a charge that went
       * through leaves one: a rejection is never written to the transactions this date comes
       * from, and Nedarim's list of orders carries no date of its own. So a failed row shows
       * why and not when - putting the last successful charge's date under the word "failed"
       * would date the failure to the day the money actually arrived.
       */
      /*
       * An accounting-system order says it with a dot and the date (HAT-1135, the office on
       * 6.10.2026: «a date instead of "הוחזר", with a red dot beside it»): green, the collection
       * went through; red, the bank sent it back. The word stays on the dot's tooltip and for
       * screen readers; the second line is left to the bank's reason, when it gave one.
       */
      if (isAptOrder(order) && date && (status === 'succeeded' || status === 'failed')) {
        const dotId = `last-charge-dot-${order.standingOrderId}`;
        return (
          <span className={styles.cellStack}>
            <span className="flex items-center gap-2 min-w-0">
              <span
                id={dotId}
                className="w-[8px] h-[8px] rounded-full flex-shrink-0"
                style={{ backgroundColor: LAST_CHARGE_COLORS[status] }}
                aria-hidden="true"
              />
              <CustomTooltip targetId={dotId}>{statusLabel}</CustomTooltip>
              <span className="truncate">{date}</span>
              <span className="sr-only">{statusLabel}</span>
            </span>
            {reason && (
              <>
                <span id={id} className={styles.subLine} tabIndex={0}>{reason}</span>
                <CustomTooltip targetId={id}>{reason}</CustomTooltip>
              </>
            )}
          </span>
        );
      }

      let note: string | null = null;
      if (status === 'failed') note = reason;
      else if (date) note = t('standingOrders.lastChargeOn', { date });

      return (
        <span className={styles.cellStack}>
          <span className="flex items-center gap-2 min-w-0">
            <span
              className="w-[8px] h-[8px] rounded-full flex-shrink-0"
              style={{ backgroundColor: LAST_CHARGE_COLORS[status] }}
            />
            <span className="truncate">{statusLabel}</span>
          </span>
          {note && (
            <>
              {/* A rejection from Nedarim is a sentence, so the line truncates and the
                  whole of it stays available in the tooltip. */}
              <span id={id} className={styles.subLine} tabIndex={0}>{note}</span>
              <CustomTooltip targetId={id}>{note}</CustomTooltip>
            </>
          )}
        </span>
      );
    },
    meta: { className: 'min-w-[150px] w-[14.5%]' },
  };
}

export function activeStatusColumn<T extends NedarimStandingOrderDtoIn>({ t, locale, frozenTerm }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'activeStatus',
    header: Col(t('standingOrders.status')),
    accessorFn: (row) => getActiveStatus(row),
    cell: ({ row }) => {
      const order = row.original;
      const status = getActiveStatus(order);
      const label = t(activeStatusKey(status, frozenTerm));
      /*
       * Why the office stopped it, under the word (HAT-1439: the office asked for «סיבת הקפאה»
       * on the row, from «מצב הוראה» in APT). The reason is short («ביטול הרשאה»); the day it
       * was set is known for few orders, so it waits in the tooltip - which also carries the
       * whole of it at compact density, where the second line is dropped. A freeze made from the
       * board says so (HAT-1441), so it is not taken for one of the office's.
       */
      /*
       * A credit order the declined-orders check froze says so the same way (HAT-1447): how many
       * months it was refused under the word, and when it was frozen and the last refusal in the
       * tooltip. Nedarim keep no reason for a credit freeze; this is our own log.
       */
      const autoFreeze = status === 'disabled' ? order.autoFreeze : null;
      const boardReason = order.aptBoardStopReason;
      const reason = status === 'cancelled' ? boardReason ?? null
        : status !== 'disabled' ? null
          : autoFreeze ? t('standingOrders.autoFrozenReason', { count: autoFreeze.months })
            : order.aptFrozenFromSite
              ? (boardReason ? t('standingOrders.aptFrozenFromSiteWithReason', { reason: boardReason }) : t('standingOrders.aptFrozenFromSite'))
            : order.aptStopReason;
      if (!reason) {
        return <StatusCell id={`active-status-${order.standingOrderId}`} color={ACTIVE_COLORS[status]} label={label} />;
      }
      const reasonDate = parseDate(order.aptStatusDate ?? null);
      const note = autoFreeze
        ? describeAutoFreeze(autoFreeze, locale, t)
        : reasonDate
          ? t('standingOrders.aptStopReasonSince', { reason, date: formatDate(reasonDate, locale) })
          : reason;
      const reasonId = `active-status-reason-${order.standingOrderId}`;
      return (
        <span className={styles.cellStack}>
          <StatusCell id={`active-status-${order.standingOrderId}`} color={ACTIVE_COLORS[status]} label={label} note={note} />
          <span id={reasonId} className={styles.subLine} tabIndex={0}>
            <span className={styles.subLineText}>{reason}</span>
          </span>
          <CustomTooltip targetId={reasonId}>{note}</CustomTooltip>
        </span>
      );
    },
    meta: { className: 'min-w-[100px] w-[11.9%]' },
  };
}

export function nextChargeColumn<T extends NedarimStandingOrderDtoIn>({ t, locale }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'nextChargeDate',
    header: Col(t('standingOrders.nextChargeDate')),
    accessorFn: (row) => {
      const d = getNextChargeDate(row);
      return d ? formatDate(d, locale) : undefined;
    },
    sortUndefined: 'last',
    sortingFn: (a, b) => compareNextChargeDateSort(a.original, b.original),
    /*
     * Nedarim report the next date; the accounting system does not, so for its orders the
     * date is projected from the billing day (aptStandingOrders.nextAptCharge) and says so -
     * the bank collects a day late over a weekend or holiday, and only once the office
     * transmits the batch.
     */
    cell: ({ row, getValue }) => {
      const value = getValue() as string;
      if (!value) return <span>-</span>;
      if (!isAptOrder(row.original)) return <span className="truncate">{value}</span>;
      return (
        <span className={styles.cellStack}>
          <span className={styles.cellMain}>{value}</span>
          <span className={styles.subLine}>{t('standingOrders.expectedCharge')}</span>
        </span>
      );
    },
    meta: { className: 'min-w-[100px] w-[11.4%]' },
  };
}

export function paymentStatusColumn<T extends NedarimStandingOrderDtoIn>({ t }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'paymentStatus',
    header: Col(t('standingOrders.paymentStatus')),
    accessorFn: (row) => formatPaymentStatus(row, t),
    cell: ({ row, getValue }) => {
      const order = row.original;
      const paid = getPaymentsPaid(order);
      const remaining = order.remainingPayments;
      const unlimited = remaining == null || isUnlimited(remaining);
      const total = unlimited ? null : getPaymentsTotal(order);
      const percent = total && total > 0
        ? Math.min(100, Math.round((paid / total) * 100)) : null;
      return (
        <span className={styles.cellStack}>
          <span className={styles.cellMain}>{getValue() as string}</span>
          {/* An order that runs until the donor stops has no proportion to draw. */}
          {unlimited ? (
            <span className={styles.subLine}>{t('standingOrders.unlimited')}</span>
          ) : percent != null && (
            <span
              className={styles.progress}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={getValue() as string}
            >
              <span style={{ width: `${percent}%` }} />
            </span>
          )}
        </span>
      );
    },
    meta: { className: 'min-w-[130px] w-[13.6%]', hiddenOnMobile: true },
  };
}

export function totalPaidColumn<T extends NedarimStandingOrderDtoIn>({ t, locale }: BoardColumnContext): ColumnDef<T> {
  return {
    id: 'totalPaid',
    header: Col(t('standingOrders.totalPaid')),
    accessorFn: (row) => formatRowMoney(getTotalPaid(row), row, locale),
    sortingFn: (a, b) => (getTotalPaid(a.original) ?? 0) - (getTotalPaid(b.original) ?? 0),
    cell: Cell(cellType.String),
    meta: { className: 'min-w-[120px] w-[11.4%]', hiddenOnMobile: true },
  };
}

/** Every column of the board after the first, in the board's order - the first says whose order it is. */
export function orderColumns<T extends NedarimStandingOrderDtoIn>(ctx: BoardColumnContext): ColumnDef<T>[] {
  return [
    paymentAmountColumn<T>(ctx),
    lastChargeColumn<T>(ctx),
    activeStatusColumn<T>(ctx),
    nextChargeColumn<T>(ctx),
    paymentStatusColumn<T>(ctx),
    totalPaidColumn<T>(ctx),
  ];
}

// ------------------------------------------------------------------------------------- export

/**
 * Everything the row holds, not only what fits on screen: this is the sheet the office reconciles
 * against, and the columns it used to get from the old site. `leading` goes first - the fund, on a
 * sheet that spans funds.
 */
export function buildExportColumns<T extends NedarimStandingOrderDtoIn>(
  { t, locale, frozenTerm }: BoardColumnContext,
  isBank: boolean,
  leading: ColumnDef<T>[] = [],
): ColumnDef<T>[] {
  return [
    ...leading,
    { id: 'donor', header: 'standingOrders.donor', accessorFn: (row) => row.clientName ?? '' },
    // The old site's bank sheet had it, and it is how the office finds the donor in its own system.
    ...(isBank
      ? [{
        id: 'donorNumber',
        header: 'standingOrders.donorNumber',
        accessorFn: (row: T) => row.aptContactId != null ? String(row.aptContactId) : '',
      }]
      : []),
    { id: 'israeliId', header: 'standingOrders.newOrder.israeliId', accessorFn: (row) => row.israeliId ?? '' },
    { id: 'phone', header: 'standingOrders.newOrder.phone', accessorFn: (row) => row.phone ?? '' },
    { id: 'email', header: 'standingOrders.newOrder.email', accessorFn: (row) => row.email ?? '' },
    { id: 'city', header: 'standingOrders.newOrder.city', accessorFn: (row) => row.city ?? '' },
    {
      id: 'paymentMethod',
      header: 'standingOrders.paymentMethod',
      accessorFn: (row) => row.type === STANDING_ORDER_TYPE_CREDIT ? t('standingOrders.credit')
        : row.type === STANDING_ORDER_TYPE_BANK ? t('standingOrders.bank') : row.type,
    },
    {
      id: 'paymentDetails',
      header: 'standingOrders.paymentDetails',
      accessorFn: (row) => row.type === STANDING_ORDER_TYPE_CREDIT
        ? [row.lastNum && `····${row.lastNum}`, row.validity].filter(Boolean).join(' ')
        : row.accountDetails ?? '',
    },
    { id: 'billingDay', header: 'standingOrders.newOrder.billingDay', accessorFn: (row) => row.billingDay ?? '' },
    { id: 'amount', header: 'standingOrders.paymentAmount', accessorFn: (row) => formatRowMoney(row.amount, row, locale) },
    { id: 'totalPaid', header: 'standingOrders.totalPaid', accessorFn: (row) => formatRowMoney(getTotalPaid(row), row, locale) },
    { id: 'paymentStatus', header: 'standingOrders.paymentStatus', accessorFn: (row) => formatPaymentStatus(row, t) },
    { id: 'activeStatus', header: 'standingOrders.activeStatus', accessorFn: (row) => t(activeStatusKey(getActiveStatus(row), frozenTerm)) },
    { id: 'lastCharge', header: 'standingOrders.lastCharge', accessorFn: (row) => t(`standingOrders.lastCharge_${getLastChargeStatus(row)}`) },
    {
      id: 'lastChargeDate',
      header: 'standingOrders.lastChargeDate',
      accessorFn: (row) => {
        const lastDate = getVisibleLastChargeDate(row);
        return lastDate ? formatDate(lastDate, locale) : '';
      },
    },
    {
      id: 'lastChargeError',
      header: 'standingOrders.chargeFailureReason',
      accessorFn: (row) => row.errorMessage ?? '',
    },
    { id: 'creationDate', header: 'standingOrders.creationDate', accessorFn: (row) => getCreationDateRaw(row) ?? '' },
    {
      id: 'nextChargeDate',
      header: 'standingOrders.nextChargeDate',
      accessorFn: (row) => parseDate(row.nextDate) ?? '',
    },
    {
      id: 'endDate',
      header: 'standingOrders.endDate',
      accessorFn: (row) => {
        const endDate = getEndDate(row);
        if (endDate) return endDate;
        return isUnlimited(row.remainingPayments) ? t('standingOrders.unlimited') : '';
      },
    },
    { id: 'comments', header: 'standingOrders.notes', accessorFn: (row) => row.comments ?? '' },
    // The office's own status text travels with the bank sheet: it is what they reconcile against.
    ...(isBank
      ? [
        { id: 'aptStatus', header: 'standingOrders.aptStatus', accessorFn: (row: T) => row.aptStatus ?? '' },
        {
          id: 'aptStatusDate',
          header: 'standingOrders.aptStatusDate',
          accessorFn: (row: T) => {
            const d = parseDate(row.aptStatusDate ?? null);
            return d ? formatDate(d, locale) : '';
          },
        },
      ]
      : []),
    {
      id: 'standingOrderId',
      header: isBank ? 'standingOrders.aptOrderId' : 'standingOrders.nedarimPlusStandingOrderId',
      accessorFn: (row) => String(row.standingOrderId),
    },
  ];
}
